import { useState } from "react";
import { Link } from "react-router-dom";
import AppLayout from "@/components/layout/AppLayout";
import { useDemoState } from "@/data/demoState";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Package, QrCode, Upload, CheckCircle2, Clock, ArrowRight, Info, Users,
  Eye, ShieldCheck, Send, Download, Printer, Link2, Image, AlertTriangle,
  Camera, Sparkles, HardHat, FileText, Shield, Truck, MapPin, XCircle
} from "lucide-react";

const packStages = [
  { id: "pre-slab", label: "Pre-Slab", itemsNeeded: 3, itemsUploaded: 1 },
  { id: "pre-frame", label: "Pre-Frame", itemsNeeded: 2, itemsUploaded: 0 },
  { id: "pre-oc", label: "Pre-OC", itemsNeeded: 2, itemsUploaded: 0 },
];

const siteItems = [
  { id: "s1", title: "Footing reinforcement photo", stage: "Pre-Slab", who: "Site Supervisor", status: "uploaded", why: "Required by condition 2.2 for soil/footing verification", uploadedDate: "18/01/2025" },
  { id: "s2", title: "Slab formwork check photo", stage: "Pre-Slab", who: "Site Supervisor", status: "needed", why: "Required before concreter mobilisation. Linked to pre-slab readiness." },
  { id: "s3", title: "Service connection photos", stage: "Pre-Slab", who: "Site Supervisor", status: "needed", why: "Stormwater and drainage connections must be documented for engineer review." },
  { id: "s4", title: "Frame tie-down photo", stage: "Pre-Frame", who: "Site Supervisor", status: "needed", why: "Required for frame inspection readiness and surveyor notification." },
  { id: "s5", title: "Waterproofing membrane photo", stage: "Pre-Frame", who: "Site Supervisor", status: "needed", why: "Required before tiler can be released." },
];

const SITE_IMAGE = "https://images.unsplash.com/photo-1762407785760-3e4c22aede86?w=400&q=80";

export default function SiteReadyPack() {
  const [selectedStage, setSelectedStage] = useState("pre-slab");
  const { trades, conditions, tradeReadiness } = useDemoState();

  return (
    <AppLayout>
      <div className="space-y-5" data-testid="site-ready-pack-page">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <Card className="border-blue-100 bg-blue-50/30">
            <CardContent className="py-3 px-4 flex items-start gap-2.5">
              <Info className="w-4 h-4 text-blue-600 mt-0.5 shrink-0" />
              <div>
                <p className="text-xs font-semibold text-blue-700 mb-0.5">What this page does</p>
                <p className="text-xs text-blue-600">Give site teams a simple pack showing what must be done, what proof is needed, and how to upload it.</p>
              </div>
            </CardContent>
          </Card>
          <Card className="border-slate-200 bg-slate-50/30">
            <CardContent className="py-3 px-4 flex items-start gap-2.5">
              <ArrowRight className="w-4 h-4 text-slate-500 mt-0.5 shrink-0" />
              <div>
                <p className="text-xs font-semibold text-slate-600 mb-0.5">What happens next</p>
                <p className="text-xs text-slate-500">Site supervisor scans QR, uploads photo. Upload lands in Proof Review. Office reviewer approves. Stage readiness may clear.</p>
              </div>
            </CardContent>
          </Card>
          <Card className="border-slate-200 bg-slate-50/30">
            <CardContent className="py-3 px-4 flex items-start gap-2.5">
              <Users className="w-4 h-4 text-slate-500 mt-0.5 shrink-0" />
              <div>
                <p className="text-xs font-semibold text-slate-600 mb-0.5">Who uses this</p>
                <p className="text-xs text-slate-500">Builder PM generates the pack. Site supervisor receives and uploads. Office admin reviews.</p>
              </div>
            </CardContent>
          </Card>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
          {/* Left: Pack stage selector + items */}
          <div className="lg:col-span-2 space-y-4">
            {/* Stage selector */}
            <div className="flex gap-3">
              {packStages.map(s => (
                <Card
                  key={s.id}
                  className={`flex-1 cursor-pointer transition-all ${selectedStage === s.id ? "border-blue-500 bg-blue-50/30 shadow-md" : "border-slate-200 hover:border-slate-300"}`}
                  onClick={() => setSelectedStage(s.id)}
                  data-testid={`pack-stage-${s.id}`}
                >
                  <CardContent className="py-3 px-4 text-center">
                    <p className="text-xs font-semibold text-slate-700">{s.label}</p>
                    <p className={`text-lg font-heading font-bold ${s.itemsUploaded < s.itemsNeeded ? "text-amber-600" : "text-emerald-600"}`}>{s.itemsUploaded}/{s.itemsNeeded}</p>
                    <p className="text-[10px] text-slate-400">items uploaded</p>
                  </CardContent>
                </Card>
              ))}
            </div>

            {/* Condition list for site items */}
            <Card className="border-slate-200" data-testid="site-items-list">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-heading">
                  Items for {packStages.find(s => s.id === selectedStage)?.label}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {siteItems.filter(s => s.stage === packStages.find(ps => ps.id === selectedStage)?.label).map(item => (
                  <div key={item.id} className="bg-white border border-slate-200 rounded-lg p-4 hover:shadow-sm transition-all" data-testid={`site-item-${item.id}`}>
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex items-center gap-2">
                        {item.status === "uploaded" ? (
                          <CheckCircle2 className="w-5 h-5 text-emerald-500" />
                        ) : (
                          <Clock className="w-5 h-5 text-amber-500" />
                        )}
                        <h4 className="text-sm font-medium text-slate-900">{item.title}</h4>
                      </div>
                      <Badge className={item.status === "uploaded" ? "bg-emerald-50 text-emerald-700 border-emerald-200 text-[10px]" : "bg-amber-50 text-amber-700 border-amber-200 text-[10px]"}>
                        {item.status === "uploaded" ? "Uploaded" : "Needed"}
                      </Badge>
                    </div>
                    <div className="space-y-1.5 ml-7">
                      <p className="text-xs text-slate-500"><span className="font-medium text-slate-600">Who should upload:</span> {item.who}</p>
                      <p className="text-xs text-slate-500"><span className="font-medium text-slate-600">Why this matters:</span> {item.why}</p>
                      {item.uploadedDate && <p className="text-xs text-slate-400">Uploaded: {item.uploadedDate}</p>}
                    </div>
                    {item.status === "uploaded" && (
                      <div className="ml-7 mt-3 flex items-center gap-3">
                        <div className="w-16 h-16 bg-slate-100 rounded-lg overflow-hidden border border-slate-200">
                          <img src={SITE_IMAGE} alt={item.title} className="w-full h-full object-cover" />
                        </div>
                        <div className="flex items-center gap-1.5">
                          <Sparkles className="w-3 h-3 text-blue-500" />
                          <span className="text-[10px] text-blue-600 font-medium">AI matched to condition 2.2 (78%)</span>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </CardContent>
            </Card>

            {/* Visual proof examples */}
            <Card className="border-slate-200" data-testid="proof-examples">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-heading flex items-center gap-2">
                  <Camera className="w-4 h-4 text-blue-600" /> Photo quality guide
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <div className="rounded-xl overflow-hidden border-2 border-emerald-200 relative">
                      <img src={SITE_IMAGE} alt="Acceptable photo" className="w-full h-32 object-cover" />
                      <div className="absolute top-2 left-2">
                        <Badge className="bg-emerald-600 text-white border-0 text-[9px]">Good example</Badge>
                      </div>
                    </div>
                    <p className="text-xs text-emerald-700 font-medium">Clear, well-lit, full coverage</p>
                    <p className="text-[10px] text-slate-500">Wide angle showing the full item in daylight</p>
                  </div>
                  <div className="space-y-2">
                    <div className="rounded-xl overflow-hidden border-2 border-red-200 relative bg-slate-200">
                      <div className="w-full h-32 flex flex-col items-center justify-center bg-slate-300/50 backdrop-blur-sm">
                        <Image className="w-8 h-8 text-slate-400 mb-1" />
                        <p className="text-[10px] text-slate-500 font-medium">Blurry / dark example</p>
                      </div>
                      <div className="absolute top-2 left-2">
                        <Badge className="bg-red-600 text-white border-0 text-[9px]">Weak example</Badge>
                      </div>
                    </div>
                    <p className="text-xs text-red-700 font-medium">Too close, blurry, or poor lighting</p>
                    <p className="text-[10px] text-slate-500">Will be flagged by AI and need retake</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Trade Pack Section */}
            <Card className="border-teal-200 bg-teal-50/20" data-testid="trade-pack-section">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-heading flex items-center gap-2 text-teal-800">
                  <HardHat className="w-4 h-4" /> Trade Pack — Tradie View
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <p className="text-xs text-teal-700 mb-2">What each trade partner sees via their SiteReady Trade Pass:</p>
                {trades.filter(t => t.projectId === 'proj-042' || t.projectId === 'proj-001').slice(0, 3).map(t => {
                  const rc = tradeReadiness.find(r => r.tradeId === t.id);
                  const stageConditions = conditions.filter(c => c.projectId === t.projectId && c.stage === t.stage);
                  const clearedCount = stageConditions.filter(c => c.status === 'cleared').length;
                  return (
                    <div key={t.id} className="bg-white border border-teal-100 rounded-lg p-3" data-testid={`trade-pack-${t.id}`}>
                      <div className="flex items-center gap-2 mb-2">
                        <Truck className="w-3.5 h-3.5 text-teal-600" />
                        <span className="text-xs font-bold text-slate-800">{t.trade}</span>
                        <span className="text-[10px] text-slate-500">{t.company}</span>
                        <Badge className={`ml-auto text-[8px] ${t.status === 'checked-in' ? 'bg-teal-100 text-teal-700' : t.status === 'ready' ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-600'}`}>
                          {t.status === 'checked-in' ? 'On Site' : t.status === 'ready' ? 'Ready' : t.status}
                        </Badge>
                      </div>
                      <div className="grid grid-cols-2 gap-2 text-[10px]">
                        <div className="flex items-center gap-1.5 text-slate-600">
                          <FileText className="w-3 h-3 text-slate-400" />
                          <span>Site notes + safety brief</span>
                        </div>
                        <div className="flex items-center gap-1.5 text-slate-600">
                          <Eye className="w-3 h-3 text-slate-400" />
                          <span>Drawings & selections</span>
                        </div>
                        <div className="flex items-center gap-1.5 text-slate-600">
                          <Shield className="w-3 h-3 text-slate-400" />
                          <span>{clearedCount}/{stageConditions.length} conditions cleared</span>
                        </div>
                        <div className="flex items-center gap-1.5 text-slate-600">
                          <Camera className="w-3 h-3 text-slate-400" />
                          <span>Proof checklist</span>
                        </div>
                      </div>
                      {rc && (
                        <div className="mt-2 flex items-center gap-2">
                          <div className="flex-1 bg-slate-100 rounded-full h-1.5">
                            <div className={`h-full rounded-full ${rc.overallStatus === 'ready' ? 'bg-emerald-500' : rc.overallStatus === 'partial' ? 'bg-amber-500' : 'bg-red-500'}`}
                              style={{ width: `${Math.round(rc.checks.filter(c => c.status === 'pass').length / rc.checks.length * 100)}%` }} />
                          </div>
                          <span className="text-[9px] text-slate-500">{rc.checks.filter(c => c.status === 'pass').length}/{rc.checks.length} checks</span>
                        </div>
                      )}
                      {t.siteReadyLink && (
                        <Link to={`/builder/trade-pass/${t.id}`}>
                          <Button variant="outline" size="sm" className="mt-2 text-[10px] h-6 w-full text-teal-600 border-teal-200 hover:bg-teal-50">
                            <Eye className="w-2.5 h-2.5 mr-1" /> Preview Trade Pass
                          </Button>
                        </Link>
                      )}
                    </div>
                  );
                })}
              </CardContent>
            </Card>
          </div>

          {/* Right: QR + explanation */}
          <div className="space-y-4">
            {/* QR preview */}
            <Card className="border-slate-200 text-center" data-testid="qr-preview">
              <CardContent className="pt-6 pb-5 px-5">
                <div className="w-32 h-32 bg-slate-50 border-2 border-slate-200 rounded-xl flex items-center justify-center mx-auto mb-4">
                  <QrCode className="w-16 h-16 text-slate-300" />
                </div>
                <p className="font-heading font-semibold text-sm text-slate-900 mb-1">Scan to upload</p>
                <p className="text-xs text-slate-500 mb-4">Pre-Slab Pack - 12 Smith St</p>
                <div className="flex flex-col gap-2">
                  <Button size="sm" className="gap-1.5 bg-slate-900 text-white hover:bg-slate-800 w-full"><Download className="w-3.5 h-3.5" /> Generate PDF pack</Button>
                  <Button size="sm" variant="outline" className="gap-1.5 w-full"><Link2 className="w-3.5 h-3.5" /> Share secure link</Button>
                  <Button size="sm" variant="outline" className="gap-1.5 w-full"><Send className="w-3.5 h-3.5" /> Send to site contact</Button>
                </div>
              </CardContent>
            </Card>

            {/* What happens after upload */}
            <Card className="border-blue-200 bg-blue-50/20" data-testid="upload-flow-explanation">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-heading text-blue-800">What happens after QR upload</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2.5">
                {[
                  "Site supervisor scans QR or opens secure link",
                  "Upload lands in Proof waiting review",
                  "easypermit suggests the matching condition",
                  "Office reviewer checks and approves",
                  "Stage condition updates",
                  "Stage readiness may clear",
                ].map((step, i) => (
                  <div key={i} className="flex items-start gap-2.5">
                    <div className="w-5 h-5 bg-blue-100 rounded-full flex items-center justify-center text-[10px] font-bold text-blue-600 shrink-0 mt-0.5">{i + 1}</div>
                    <p className="text-xs text-blue-700">{step}</p>
                  </div>
                ))}
                <div className="border-t border-blue-100 pt-2 space-y-1">
                  <p className="text-[10px] text-blue-600">No full account required. No login needed.</p>
                  <p className="text-[10px] text-blue-600">Upload alone does not clear anything without review.</p>
                  <p className="text-[10px] text-blue-600">Who sees it: Builder PM, assigned reviewer, surveyor via pack.</p>
                </div>
              </CardContent>
            </Card>

            {/* What changes if approved */}
            <Card className="border-slate-200">
              <CardHeader className="pb-2">
                <CardTitle className="text-xs font-heading text-slate-600">What changes if approved</CardTitle>
              </CardHeader>
              <CardContent className="space-y-1.5">
                {[
                  "Linked condition status updates to cleared",
                  "Blocker count reduces",
                  "Stage readiness recalculates",
                  "Trade release may unlock",
                  "Surveyor notification readiness may update",
                ].map((item, i) => (
                  <p key={i} className="text-[10px] text-slate-600 flex items-center gap-1.5">
                    <CheckCircle2 className="w-3 h-3 text-blue-500 shrink-0" /> {item}
                  </p>
                ))}
              </CardContent>
            </Card>

            {/* What if another photo needed */}
            <Card className="border-amber-200 bg-amber-50/20">
              <CardContent className="py-3 px-4">
                <p className="text-xs font-semibold text-amber-700 mb-1">If another photo is needed</p>
                <p className="text-[10px] text-amber-600">Reviewer marks it as needing retake. Site supervisor receives a notification with the reason. A new upload link is generated.</p>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </AppLayout>
  );
}
